// Phone sheets over the terminal: compose/paste (a real text field, so iOS paste works),
// select text (the terminal as plain selectable text) and the link menu (open / copy).
// Plain module state so the terminal code can open one without React.

import { useSyncExternalStore } from 'react';
import { COARSE_MQ, matches } from './mobile';
import { openClicked } from './openLinkStore';

export type Sheet =
  | { kind: 'compose'; sid: number | null } // null: the desktop's focused app
  | { kind: 'select'; sid: number; text: string }
  | { kind: 'link'; uri: string; sid?: number };

let current: Sheet | null = null;
const listeners = new Set<() => void>();

export function openSheet(s: Sheet) {
  current = s;
  listeners.forEach((l) => l());
}

export function closeSheet() {
  if (!current) return;
  current = null;
  listeners.forEach((l) => l());
}

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
};

export const useSheet = () => useSyncExternalStore(subscribe, () => current);

const isWeb = (uri: string) => /^https?:\/\//i.test(uri);

/** A link activated in the terminal: a menu on touch devices, a new tab otherwise; from session sid, it goes where
 *  a program there opening it would (openClicked). */
export function openLink(uri: string, sid?: number) {
  if (!isWeb(uri)) return;
  if (matches(COARSE_MQ)) openSheet({ kind: 'link', uri, sid });
  else if (sid !== undefined) openClicked(sid, uri);
  else window.open(uri, '_blank', 'noopener');
}

/** Copy text; falls back to execCommand where the async clipboard API is missing or blocked. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;font-size:16px';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    ta.remove();
    return ok;
  }
}
